/**
 * Session recap sheets — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_tabletop_iii`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const sessionRecapSheetsPack: ToilePack = {
    slug: 'session-recap-sheets',
    category: 'tabletop',
    name: 'Session recap sheets',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'full', label: 'Full recap', subject: 'a sheet titled "Session Recap" with ruled sections "Where we left off", "What happened", "Loot" and "NPCs met", and a date line at the top'},
        {key: 'short', label: 'Half-page recap', subject: 'a page split into two identical half-page recap cards with a dashed cut line between, each with "Session" and three ruled lines'},
        {key: 'next', label: 'Next session', subject: 'a sheet titled "Next Session" with ruled sections "Hooks", "Encounters" and "Prep", and a small checklist box column'},
    ],
    styles: [
        {key: 'scroll', label: 'Scroll', style: 'a rolled scroll sheet with inked headings and thin ruled sections', palette: 'dark brown ink on pale parchment', framing: 'the page filled inside a wide margin'},
        {key: 'form', label: 'Clean form', style: 'a clean modern form with thin rules, bold section headings and small icons', palette: 'black ink with one blue accent on white', framing: 'the page filled inside a wide margin'},
        {key: 'tavern', label: 'Tavern board', style: 'a tavern notice-board look with tacked-on paper sections and wooden-frame edges', palette: 'dark wood brown and cream paper', framing: 'the page filled inside a wide margin'},
    ],
    directions: ['dark fantasy', 'sci-fi', 'pirate'],
    directionHint: 'try: dark fantasy',
    seo: {
        title: 'Session recap sheets: full, half-page, next session',
        metaDescription: 'Printable session recap sheets in three styles: a full recap, a half-page recap and a next-session planning sheet.',
        h1: 'Session recap sheets: full recap, half-page recap, next session',
        tagline: 'Nine sheets: a full recap, a half-page recap and a next-session sheet, in three styles.',
        intro: 'A game master fills in a session recap after each game, or reads it out at the start of the next. This pack shows a full recap sheet with ruled sections for where the party left off, loot and NPCs, a half-page recap, and a next-session sheet for hooks and encounters. Each comes as a rolled scroll, a clean form or a tavern notice board. Type a campaign, such as dark fantasy, and the sheet keeps its sections.',
        useCases: [
            'a recap read aloud at the start of a session',
            'a half-page note after a short game',
            "a plan for the next session's hooks and encounters",
            'a record of loot and NPCs across a campaign',
        ],
        faq: [
            {q: 'What do the nine pictures show?', a: 'Three readings in three styles: a full recap sheet, a page of two half-page recaps, and a next-session planning sheet.'},
            {q: 'What is the half-page recap for?', a: 'A short recap for a quick game: two identical cards on one page, each with a Session line and three ruled lines.'},
            {q: 'What goes on the next-session sheet?', a: 'Ruled sections for hooks, encounters and prep, and a small checklist column to tick off.'},
            {q: 'Which style is the plainest to print?', a: 'Clean form uses black ink with one blue accent on white.'},
        ],
    },
}
